import { useMutation } from '@apollo/react-hooks';
import { 
  Table, 
  Button, 
  Select, 
  Alert, 
  Badge, 
  Modal, 
  Text, 
  Group, 
  Stack, 
  Title, 
  Loader, 
  Divider,
  Grid,
  ThemeIcon,
  Paper,
  Container
} from '@mantine/core';
import { 
  IconCalendar, 
  IconUsers, 
  IconCheck, 
  IconAlertCircle, 
  IconClock, 
  IconArrowRight,
  IconRefresh
} from '@tabler/icons-react';
import gql from 'graphql-tag';
import { useState } from 'react';
import moment from 'moment';

const PREVIEW_YEAR_END_ROLLOVER = gql`
  mutation PreviewYearEndRollover($year: Int!) {
    previewYearEndRollover(year: $year) {
      year
      totalEmployees
      alreadyProcessedCount
      items {
        userId
        user {
          _id
          firstName
          lastName
        }
        timeOffType
        remainingBalance
        resetAmount
        rolloverAmount
        earnedAmount
        alreadyProcessed
      }
    }
  }
`;

const EXECUTE_YEAR_END_ROLLOVER = gql`
  mutation ExecuteYearEndRollover($year: Int!) {
    executeYearEndRollover(year: $year) {
      year
      success
      transactionsCreated
      errors
    }
  }
`;

const YearEndRollover = () => {
  const currentYear = moment().year();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear + 1);
  const [previewData, setPreviewData] = useState<any>(null);
  const [executeModalOpen, setExecuteModalOpen] = useState(false);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [executeResult, setExecuteResult] = useState<any>(null);

  const [previewRollover, { loading: previewLoading }] = useMutation(PREVIEW_YEAR_END_ROLLOVER, {
    onCompleted: (data) => {
      setPreviewData(data.previewYearEndRollover);
    },
    onError: (error) => {
      console.error('Preview error:', error);
    },
  });

  const [executeRollover, { loading: executeLoading }] = useMutation(EXECUTE_YEAR_END_ROLLOVER, {
    onCompleted: (data) => {
      setExecuteResult(data.executeYearEndRollover);
      setExecuteModalOpen(false);
      setResultModalOpen(true);
      // Refresh preview after execution
      previewRollover({ variables: { year: selectedYear } });
    },
    onError: (error) => {
      console.error('Execute error:', error);
      setExecuteResult({ success: false, errors: [error.message] });
      setExecuteModalOpen(false);
      setResultModalOpen(true);
    },
  });

  const handlePreview = () => {
    setPreviewData(null);
    previewRollover({ variables: { year: selectedYear } });
  };

  const handleExecute = () => {
    executeRollover({ variables: { year: selectedYear } });
  };

  // Generate year options (current year and next 2 years)
  const yearOptions = Array.from({ length: 3 }, (_, i) => ({
    value: (currentYear + i).toString(),
    label: (currentYear + i).toString(),
  }));

  const pendingItems = previewData?.items?.filter((item: any) => !item.alreadyProcessed) || [];
  const processedItems = previewData?.items?.filter((item: any) => item.alreadyProcessed) || [];

  const StatCard = ({ title, value, icon: Icon, color }: any) => (
    <Paper shadow="xs" p="md" radius="md" withBorder>
      <Group position="apart">
        <div>
          <Text color="dimmed" size="xs" transform="uppercase" weight={700}>
            {title}
          </Text>
          <Text weight={700} size="xl">
            {value}
          </Text>
        </div>
        <ThemeIcon color={color} variant="light" size={38} radius="md">
          <Icon size={24} />
        </ThemeIcon>
      </Group>
    </Paper>
  );

  return (
    <Container fluid px={0}>
      <Stack spacing="lg">
        {/* Header Section */}
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Group position="apart" align="start">
            <Group>
              <ThemeIcon size={48} radius="md" color="blue" variant="light">
                <IconCalendar size={28} />
              </ThemeIcon>
              <div>
                <Title order={2}>Year-End Time Off Rollover</Title>
                <Text color="dimmed" size="sm" mt={4} style={{ maxWidth: 600 }}>
                  Process year-end time off balances by creating RESET, ROLLOVER, and EARNED transactions for the start of a new year.
                  This resets balances to zero, applies capped rollover amounts, and adds new earned hours.
                </Text>
              </div>
            </Group>
          </Group>

          <Divider my="lg" />

          <Group align="end">
            <Select
              label="Select Target Year"
              description="Transactions will be dated January 1st"
              data={yearOptions}
              value={selectedYear.toString()}
              onChange={(value) => setSelectedYear(parseInt(value || currentYear.toString()))}
              style={{ width: 200 }}
              icon={<IconCalendar size={16} />}
            />
            <Button 
              onClick={handlePreview} 
              loading={previewLoading}
              leftIcon={<IconRefresh size={16} />}
              size="md"
            >
              Preview Rollover
            </Button>
          </Group>
        </Paper>

        {previewLoading && (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Stack align="center" spacing="md" py={32}>
              <Loader size="xl" variant="dots" />
              <Text color="dimmed" size="sm">Calculating rollover projections...</Text>
            </Stack>
          </Paper>
        )}

        {previewData && !previewLoading && (
          <Stack spacing="lg">
            {/* Stats Grid */}
            <Grid>
              <Grid.Col span={4}>
                <StatCard 
                  title="Total Employees" 
                  value={previewData.totalEmployees} 
                  icon={IconUsers}
                  color="blue"
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <StatCard 
                  title="Pending Processing" 
                  value={pendingItems.length} 
                  icon={IconClock}
                  color="yellow"
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <StatCard 
                  title="Already Processed" 
                  value={processedItems.length} 
                  icon={IconCheck}
                  color="green"
                />
              </Grid.Col>
            </Grid>

            {/* Actions & Alerts */}
            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group position="apart">
                <Text size="lg" weight={600}>
                  Preview for Year {previewData.year}
                </Text>
                
                {previewData.items?.length > 0 && (
                  <Button 
                    color="green" 
                    size="md"
                    onClick={() => setExecuteModalOpen(true)}
                    leftIcon={<IconArrowRight size={16} />}
                  >
                    {processedItems.length > 0 ? 'Rerun Rollover' : 'Execute Rollover'} ({previewData.items.length} Items)
                  </Button>
                )}
              </Group>

              {processedItems.length > 0 && pendingItems.length === 0 && (
                <Alert icon={<IconRefresh size={16} />} title="Previously Processed" color="blue" variant="light" mt="md">
                  All items for {previewData.year} have been processed. You can rerun the rollover to recalculate values if needed.
                </Alert>
              )}

              {previewData.items?.length === 0 && (
                <Alert icon={<IconAlertCircle size={16} />} title="No Data Found" color="yellow" mt="md">
                  No employees have time off settings configured or there are no balances to roll over.
                </Alert>
              )}
            </Paper>

            {/* Detailed Table */}
            {previewData.items?.length > 0 && (
              <Paper shadow="sm" radius="md" withBorder>
                <div style={{ overflowX: 'auto' }}>
                  <Table verticalSpacing="sm" highlightOnHover>
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th style={{ padding: '12px 16px' }}>Employee</th>
                        <th>Time Off Type</th>
                        <th style={{ textAlign: 'right' }}>Previous Balance</th>
                        <th style={{ textAlign: 'right' }}>Reset</th>
                        <th style={{ textAlign: 'right' }}>Rollover (+Cap)</th>
                        <th style={{ textAlign: 'right' }}>New Earned</th>
                        <th style={{ textAlign: 'right' }}>New Balance</th>
                        <th style={{ textAlign: 'center' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.items.map((item: any, index: number) => {
                        const newBalance = (item.rolloverAmount ?? 0) + (item.earnedAmount ?? 0);
                        return (
                          <tr key={`${item.userId}-${item.timeOffType}-${index}`}>
                            <td style={{ padding: '12px 16px' }}>
                              <Text weight={500}>{item.user?.firstName} {item.user?.lastName}</Text>
                            </td>
                            <td>
                              <Badge variant="outline" color="gray">{item.timeOffType}</Badge>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <Text color="dimmed">{item.remainingBalance?.toFixed(2)} hrs</Text>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <Text color="red" weight={500}>{item.resetAmount?.toFixed(2)} hrs</Text>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <Text color="blue" weight={500}>+{item.rolloverAmount?.toFixed(2)} hrs</Text>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <Text color="green" weight={500}>+{item.earnedAmount?.toFixed(2)} hrs</Text>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <Text weight={600}>{newBalance.toFixed(2)} hrs</Text>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {item.alreadyProcessed ? (
                                <Badge color="green" variant="dot">Processed</Badge>
                              ) : (
                                <Badge color="yellow" variant="dot">Pending</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Paper>
            )}
          </Stack>
        )}

        {/* Confirmation Modal */}
        <Modal
          opened={executeModalOpen}
          onClose={() => setExecuteModalOpen(false)}
          title={<Title order={3}>Confirm Year-End Rollover</Title>}
          size="lg"
        >
          <Stack spacing="md">
            <Alert icon={<IconAlertCircle size={16} />} color="yellow" title="Warning">
              You are about to {processedItems.length > 0 ? 'rerun' : 'create'} time off transactions for <b>{previewData?.items?.length}</b> employee/time-off-type combinations.
              {processedItems.length > 0 && ' Existing RESET, ROLLOVER, and EARNED transactions will be replaced.'}
            </Alert>
            <Text size="sm">
              This will create <b>RESET</b>, <b>ROLLOVER</b>, and <b>EARNED</b> transactions dated <b>January 1, {selectedYear}</b> for all items.
              The RESET transaction zeros out the previous balance before applying rollover and earned hours.
              {processedItems.length > 0 && <><br /><br /><b>Note:</b> Any TAKEN transactions on January 1 will be preserved.</>}
            </Text>
            <Group position="right" mt="xl">
              <Button variant="default" onClick={() => setExecuteModalOpen(false)}>
                Cancel
              </Button>
              <Button color="green" onClick={handleExecute} loading={executeLoading}>
                Yes, {processedItems.length > 0 ? 'Rerun' : 'Execute'} Rollover
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Result Modal */}
        <Modal
          opened={resultModalOpen}
          onClose={() => setResultModalOpen(false)}
          title={<Title order={3}>Rollover Result</Title>}
          size="md"
        >
          {executeResult && (
            <Stack spacing="md">
              {executeResult.success ? (
                <Alert icon={<IconCheck size={16} />} color="green" title="Success" variant="filled">
                  Year-end rollover completed successfully!
                </Alert>
              ) : (
                <Alert icon={<IconAlertCircle size={16} />} color="red" title="Completed with Errors" variant="filled">
                  Some errors occurred during the rollover process.
                </Alert>
              )}
              
              <Paper withBorder p="md" radius="md" bg="gray.0">
                <Stack spacing="xs">
                  <Group position="apart">
                    <Text size="sm">Transactions Created:</Text>
                    <Text weight={600}>{executeResult.transactionsCreated}</Text>
                  </Group>
                </Stack>
              </Paper>

              {executeResult.errors?.length > 0 && (
                <Alert color="red" title="Errors">
                  <Stack spacing={4}>
                    {executeResult.errors.map((error: string, index: number) => (
                      <Text key={index} size="xs">• {error}</Text>
                    ))}
                  </Stack>
                </Alert>
              )}
              
              <Button fullWidth onClick={() => setResultModalOpen(false)} mt="md">
                Close
              </Button>
            </Stack>
          )}
        </Modal>
      </Stack>
    </Container>
  );
};

export default YearEndRollover;

